import { Check, Circle, Trash2 } from 'lucide-react'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { formatRelative } from '@/utils/format'
import { cn } from '@/utils/cn'
import { splitNotificationBody } from '@/modules/notifications/utils/notification-preview'
import type { NotificationItem } from '@/modules/notifications/services/notification.service'
import { NotificationSwipeRow } from '@/modules/notifications/components/NotificationSwipeRow'

interface NotificationListItemProps {
  notification: NotificationItem
  /** panel = bell dropdown (compact), page = Notifications page. */
  variant: 'panel' | 'page'
  onOpen: (notification: NotificationItem) => void
  onToggleRead: (id: string) => void
  onDelete: (id: string) => void
}

/** Touch screens swipe; mouse screens get hover buttons (read/unread + delete). */
export function NotificationListItem({
  notification,
  variant,
  onOpen,
  onToggleRead,
  onDelete,
}: NotificationListItemProps) {
  const touch = useMediaQuery('(hover: none)')
  const isUnread = !notification.readAt
  const isPage = variant === 'page'
  const { action, detail } = splitNotificationBody(notification.body)
  const actionBtn =
    'flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'

  return (
    <NotificationSwipeRow
      enabled={touch}
      isUnread={isUnread}
      onDelete={() => onDelete(notification.id)}
      onToggleRead={() => onToggleRead(notification.id)}
    >
      <div className={cn('group relative flex w-full min-w-0 items-stretch', isUnread && 'bg-primary/5')}>
        <button
          type="button"
          onClick={() => onOpen(notification)}
          className={cn(
            'flex min-w-0 flex-1 items-start text-left transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40',
            touch ? 'active:bg-muted/60' : 'hover:bg-muted/50',
            isPage ? 'touch-target gap-3 py-3.5 pl-4 pr-4' : 'gap-2 py-2.5 pl-3 pr-3',
            !touch && (isPage ? 'pr-24' : 'pr-20'),
          )}
        >
          <span
            className={cn(
              'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
              isUnread ? 'bg-primary' : 'bg-transparent',
            )}
            aria-hidden
          />
          <span className="min-w-0 flex-1 space-y-0.5 overflow-hidden">
            <span
              className={cn(
                'block truncate text-sm tracking-tight text-foreground',
                isUnread ? 'font-semibold' : 'font-medium',
              )}
              title={notification.title}
            >
              {notification.title}
            </span>
            {action ? (
              <span className="block truncate text-xs text-muted-foreground" title={action}>
                {action}
              </span>
            ) : null}
            {detail ? (
              <span className="block truncate text-[11px] text-muted-foreground/80" title={detail}>
                {detail}
              </span>
            ) : null}
            <span className="block text-[11px] text-muted-foreground/70">
              {formatRelative(notification.createdAt)}
            </span>
          </span>
        </button>

        {!touch ? (
          <div
            className={cn(
              'absolute inset-y-0 right-2 flex items-center gap-0.5 opacity-0 transition-opacity duration-150',
              'group-hover:opacity-100 group-focus-within:opacity-100',
            )}
          >
            <button
              type="button"
              onClick={() => onToggleRead(notification.id)}
              className={actionBtn}
              aria-label={isUnread ? 'Mark read' : 'Mark unread'}
              title={isUnread ? 'Mark read' : 'Mark unread'}
            >
              {isUnread ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}
            </button>
            <button
              type="button"
              onClick={() => onDelete(notification.id)}
              className={cn(actionBtn, 'hover:text-destructive')}
              aria-label="Delete notification"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
      </div>
    </NotificationSwipeRow>
  )
}
